import type { CalculatorAxis, ProgrammaticLandingPageConfig } from './types'

/** topicId of the DE "Solaranlage Kosten" pillar page (and its ES/PL siblings) - the umbrella
 *  overview page every kWp/kWh programmatic pSEO page (Task 04/05) rolls up to (pSEO-Story
 *  Task 06, "Pillar-Page-Struktur"). A dedicated lookup helper isn't needed here since
 *  `getLandingPagesByTopic` from content/landingPages/index.ts already does the job - this
 *  constant just names the topic so callers (useLandingPageSeo.ts) don't hardcode the string.
 *  Revisit if a future rollout (Task 08/09) introduces an unrelated cluster with its own pillar. */
export const COST_CLUSTER_PILLAR_TOPIC_ID = 'solaranlage-kosten'

/** SEO-4.0-Story Task 08 made `calculatorPrefill` optional on `ProgrammaticLandingPageConfig`
 *  (the 250 city-yield pages have no size axis, see types.ts doc) - this "size series" grouping
 *  only makes sense for pages that do have one, so everything below narrows to this type first. */
type SizedProgrammaticPage = ProgrammaticLandingPageConfig & { calculatorPrefill: CalculatorAxis }

function hasSizeAxis(page: ProgrammaticLandingPageConfig): page is SizedProgrammaticPage {
  return page.calculatorPrefill != null
}

function axisValue(page: SizedProgrammaticPage): number {
  return page.calculatorPrefill.kind === 'kwp' ? page.calculatorPrefill.kwp : page.calculatorPrefill.kwh
}

/** Groups pages into the same "size series" - same language and same calculatorPrefill.kind
 *  (kWp pages and kWh-storage pages are different series, e.g. a DE kWp page's neighbors are
 *  other DE kWp pages, never a kWh page or an ES page). */
function seriesKey(page: SizedProgrammaticPage): string {
  return `${page.lang}:${page.calculatorPrefill.kind}`
}

/** The immediate neighbors of `page` within its own size series (same language, same axis kind),
 *  sorted by numeric size - e.g. the 10 kWp DE page's neighbors are whichever DE kWp pages are
 *  currently registered directly below/above it (8 and 12 today), not a fixed hand-picked pair.
 *  Computed live from `allPages` so a future rollout (Task 08/09, e.g. adding 6 kWp) is picked up
 *  everywhere automatically without hand-editing existing page configs (pSEO-Story Task 06,
 *  "vermeidet manuelle Pflege bei jeder neuen Seite" - the exact problem Umsetzungsbereich 2 of
 *  the task doc calls out). Returns 1 neighbor for the smallest/largest size in a series, 2
 *  otherwise; empty if `page` isn't found in `allPages` at all. */
export function getNeighborProgrammaticPages(
  page: ProgrammaticLandingPageConfig,
  allPages: ProgrammaticLandingPageConfig[],
): ProgrammaticLandingPageConfig[] {
  if (!hasSizeAxis(page)) return []
  // SEO-4.0-Story Task 08 added city-yield pages to the same PROGRAMMATIC_LANDING_PAGES registry
  // without a calculatorPrefill (see types.ts doc) - filter them out before seriesKey/axisValue
  // touch calculatorPrefill.kind, or every call here would throw on the first city page in the array.
  const withAxis = allPages.filter(hasSizeAxis)
  const series = withAxis.filter((p) => seriesKey(p) === seriesKey(page)).sort((a, b) => axisValue(a) - axisValue(b))
  const index = series.findIndex((p) => p.slug === page.slug)
  if (index === -1) return []

  const neighbors: ProgrammaticLandingPageConfig[] = []
  if (index > 0) neighbors.push(series[index - 1])
  if (index < series.length - 1) neighbors.push(series[index + 1])
  return neighbors
}
